import { useEffect, useState } from "react";
import { useParams } from "wouter";
import { ArrowLeft, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { PageHeader } from "@/components/layout/page-header";
import { PageTabs } from "@/components/layout/page-tabs";
import { DataCard } from "@/components/data/data-card";
import { StatCard, StatCardGrid } from "@/components/data/stat-card";
import { StatusBadge } from "@/components/data/status-badge";
import { EmptyState } from "@/components/data/empty-state";
import { estadoDaConsulta } from "@/components/data/estado-consulta";
import { useEstadoNaUrl } from "@/lib/estado-na-url";
import { useNavegacao } from "@/lib/navegacao";
import { MODULES } from "@/lib/modules";
import {
  useAtualizarFazenda, useCortesia, useFazenda, useAcaoUsuarioFazenda,
  useAcessosDaFazenda, useSolicitarAcesso, useEntrarComo, type AcessoSuporteDTO,
} from "@/hooks/use-plataforma";
import { useAuth } from "@/lib/auth-context";
import { LinkAcessoDialog, type LinkAcesso } from "@/components/admin/link-acesso-dialog";
import { Eye, KeyRound, UserCheck, UserX, ShieldCheck } from "lucide-react";
import { BadgeStatusOrg, fmtData, fmtUltimoAcesso } from "./fazendas";
import { ROTULO_ORIGEM_TRIAL, type FichaFazendaDTO } from "@shared/plataforma-fazendas";
import { labelOrgTipo } from "@shared/plataforma-organizacoes";
import { MODULOS_USUARIO } from "@shared/schema";

// ⚠️ Constantes de MÓDULO (deps de useMemo dentro do hook).
const PADRAO = { aba: "cadastro" };
const PERMITIDOS = { aba: ["cadastro", "plano", "usuarios", "acessos", "auditoria"] } as const;

const ROTULO_PAPEL: Record<string, string> = {
  ADMIN: "Admin", GESTOR: "Gestor", ENCARREGADO: "Encarregado", CAMPO: "Campo", LEITURA: "Leitura",
};

function rotuloModulo(id: string): string {
  return MODULES.find(m => m.id === id)?.label ?? id;
}

/**
 * Plataforma › Fazendas › ficha. Quatro chips (Cadastro · Plano e módulos ·
 * Usuários · Auditoria), a aba na URL (`?aba=`). Usuários é SÓ LEITURA neste
 * ciclo — as ações (reset, desativar) chegam no P2.
 */
export default function PlataformaFazenda() {
  const { id } = useParams<{ id: string }>();
  const { voltar } = useNavegacao();
  const { estado, definir } = useEstadoNaUrl(PADRAO, PERMITIDOS);
  const consulta = useFazenda(id);
  const aguardando = estadoDaConsulta(consulta, { variante: "painel" });

  if (aguardando) return <div className="space-y-6"><PageHeader titulo="Fazenda" breadcrumb="Plataforma / Fazendas" />{aguardando}</div>;
  const f = consulta.data!;

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumb="Plataforma / Fazendas"
        titulo={f.nome}
        descricao={`${labelOrgTipo(f.tipo)}${f.cidade ? ` · ${f.cidade}` : ""} · ${f.slug}`}
        acoesSecundarias={
          <Button variant="outline" onClick={() => voltar("/plataforma/fazendas")} data-testid="button-voltar-fazendas">
            <ArrowLeft className="w-4 h-4 mr-2" />Fazendas
          </Button>
        }
        acao={<BotaoEntrarComo f={f} />}
      />

      <StatCardGrid>
        <StatCard rotulo="Status" valor={f.ativo ? "" : "Inativa"} legenda={<BadgeStatusOrg f={f} />} testId="stat-status" />
        <StatCard rotulo="Usuários" valor={`${f.usuariosAtivos}/${f.usuariosTotal}`} unidade="ativos" testId="stat-usuarios" />
        <StatCard rotulo="Último acesso" valor={fmtUltimoAcesso(f.ultimoAcesso)} testId="stat-ultimo-acesso" />
        <StatCard rotulo="Criada" valor={fmtData(f.createdAt)} legenda={<span>{ROTULO_ORIGEM_TRIAL[f.origemTrial] ?? f.origemTrial}</span>} testId="stat-criada" />
      </StatCardGrid>

      <PageTabs
        ativa={estado.aba}
        onChange={aba => definir({ aba })}
        abas={[
          { id: "cadastro", label: "Cadastro" },
          { id: "plano", label: "Plano e módulos" },
          { id: "usuarios", label: "Usuários" },
          { id: "acessos", label: "Acessos de suporte" },
          { id: "auditoria", label: "Auditoria" },
        ]}
      />

      {estado.aba === "cadastro" && <AbaCadastro f={f} />}
      {estado.aba === "plano" && <AbaPlano f={f} />}
      {estado.aba === "usuarios" && <AbaUsuarios f={f} />}
      {estado.aba === "acessos" && <AbaAcessos f={f} />}
      {estado.aba === "auditoria" && <AbaAuditoria f={f} />}
    </div>
  );
}

function AbaCadastro({ f }: { f: FichaFazendaDTO }) {
  const { toast } = useToast();
  const atualizar = useAtualizarFazenda(f.id);
  const [nome, setNome] = useState(f.nome);
  const [tipo, setTipo] = useState(f.tipo);
  const [cnpjCpf, setCnpjCpf] = useState(f.cnpjCpf ?? "");
  const [telefone, setTelefone] = useState(f.telefoneWhatsapp ?? "");
  const [cidade, setCidade] = useState(f.cidade ?? "");
  const [praca, setPraca] = useState(f.praca ?? "");
  const [obs, setObs] = useState(f.observacoesPlataforma ?? "");
  const [ativo, setAtivo] = useState(f.ativo);

  // A ficha recarrega depois de salvar: sincroniza o formulário com o dado novo.
  useEffect(() => {
    setNome(f.nome); setTipo(f.tipo); setCnpjCpf(f.cnpjCpf ?? ""); setTelefone(f.telefoneWhatsapp ?? "");
    setCidade(f.cidade ?? ""); setPraca(f.praca ?? ""); setObs(f.observacoesPlataforma ?? ""); setAtivo(f.ativo);
  }, [f]);

  function salvar() {
    atualizar.mutate(
      {
        nome: nome.trim(),
        ...(tipo !== f.tipo ? { tipo } : {}),
        cnpjCpf: cnpjCpf.trim() || null,
        telefoneWhatsapp: telefone.trim() || null,
        cidade: cidade.trim() || null,
        praca: praca.trim() || null,
        observacoesPlataforma: obs.trim() || null,
        ativo,
      },
      {
        onSuccess: () => toast({ title: "Cadastro salvo" }),
        onError: (e: Error) => toast({ title: "Não deu para salvar", description: e.message, variant: "destructive" }),
      },
    );
  }

  return (
    <DataCard titulo="Cadastro" meta="O que o Rizoma sabe da fazenda">
      <div className="p-4 grid gap-4 md:grid-cols-2">
        <div className="space-y-1.5 md:col-span-2">
          <Label htmlFor="nome">Nome</Label>
          <Input id="nome" value={nome} onChange={e => setNome(e.target.value)} data-testid="input-fazenda-nome" />
        </div>
        <div className="space-y-1.5">
          <Label>Tipo</Label>
          <Select value={tipo} onValueChange={setTipo}>
            <SelectTrigger data-testid="select-fazenda-tipo"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="PRODUTOR">Produtor</SelectItem>
              <SelectItem value="COMPRADOR">Comprador</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">Só muda enquanto a org não tiver romaneios nem vínculos no Mercado.</p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="cnpj">CNPJ / CPF</Label>
          <Input id="cnpj" value={cnpjCpf} onChange={e => setCnpjCpf(e.target.value)} data-testid="input-fazenda-cnpj" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="tel">WhatsApp</Label>
          <Input id="tel" type="tel" inputMode="tel" value={telefone} onChange={e => setTelefone(e.target.value)} placeholder="(74) 99999-8888" data-testid="input-fazenda-telefone" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="cidade">Cidade</Label>
          <Input id="cidade" value={cidade} onChange={e => setCidade(e.target.value)} data-testid="input-fazenda-cidade" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="praca">Praça padrão (slug)</Label>
          <Input id="praca" value={praca} onChange={e => setPraca(e.target.value)} placeholder="irece" data-testid="input-fazenda-praca" />
        </div>
        <div className="space-y-1.5 md:col-span-2">
          <Label htmlFor="obs">Observações da plataforma</Label>
          <Textarea id="obs" value={obs} onChange={e => setObs(e.target.value)} rows={3} placeholder="Nota interna — a fazenda nunca vê." data-testid="input-fazenda-obs" />
        </div>
        <div className="flex items-center gap-3 md:col-span-2">
          <Switch id="ativo" checked={ativo} onCheckedChange={setAtivo} data-testid="switch-fazenda-ativo" />
          <Label htmlFor="ativo">Organização ativa</Label>
          {!ativo && <StatusBadge tom="erro">inativa: a API responde 402 para todo mundo</StatusBadge>}
        </div>
      </div>
      <div className="flex justify-end gap-2 border-t px-4 py-3">
        <Button onClick={salvar} disabled={atualizar.isPending || nome.trim().length < 2} data-testid="button-salvar-cadastro">
          {atualizar.isPending ? "Salvando…" : "Salvar cadastro"}
        </Button>
      </div>
    </DataCard>
  );
}

function AbaPlano({ f }: { f: FichaFazendaDTO }) {
  const { toast } = useToast();
  const cortesia = useCortesia(f.id);
  const atualizar = useAtualizarFazenda(f.id);
  const [aberto, setAberto] = useState(false);
  const [regime, setRegime] = useState<"permanente" | "ate" | "revogar">(f.trialEndsAt ? "ate" : "permanente");
  const [ate, setAte] = useState(f.trialEndsAt ? f.trialEndsAt.slice(0, 10) : "");
  const [modulos, setModulos] = useState<string[]>(f.modulesEnabled);

  useEffect(() => { setModulos(f.modulesEnabled); }, [f]);

  function alternar(id: string) {
    setModulos(prev => (prev.includes(id) ? prev.filter(m => m !== id) : [...prev, id]));
  }

  function salvarModulos() {
    atualizar.mutate({ modulesEnabled: modulos }, {
      onSuccess: () => toast({ title: "Módulos salvos" }),
      onError: (e: Error) => toast({ title: "Não deu para salvar", description: e.message, variant: "destructive" }),
    });
  }

  function salvarCortesia() {
    const trialEndsAt = regime === "permanente" ? null : regime === "revogar" ? "revoke" : (ate ? new Date(ate + "T23:59:59").toISOString() : null);
    if (regime === "ate" && !ate) {
      toast({ title: "Informe até quando", variant: "destructive" });
      return;
    }
    cortesia.mutate({ trialEndsAt }, {
      onSuccess: () => { setAberto(false); toast({ title: "Cortesia salva" }); },
      onError: (e: Error) => toast({ title: "Não deu para salvar", description: e.message, variant: "destructive" }),
    });
  }

  return (
    <div className="space-y-4">
      <DataCard titulo="Plano" meta={`plano ${f.plano}`}>
        <div className="p-4 flex flex-wrap items-center gap-3">
          <BadgeStatusOrg f={f} />
          <span className="text-sm text-muted-foreground">
            {f.hasSubscription
              ? "Assinatura Stripe ativa."
              : f.trialEndsAt
                ? `Acesso liberado até ${fmtData(f.trialEndsAt)} (${ROTULO_ORIGEM_TRIAL[f.origemTrial] ?? f.origemTrial}).`
                : `Cortesia permanente (${ROTULO_ORIGEM_TRIAL[f.origemTrial] ?? f.origemTrial}).`}
          </span>
          <Button variant="outline" size="sm" className="ml-auto" onClick={() => setAberto(true)} data-testid="button-editar-cortesia">
            Editar cortesia
          </Button>
        </div>
      </DataCard>

      <DataCard titulo="Módulos" meta="O que a org tem — os usuários dela podem ser restringidos dentro disto">
        <div className="p-4 space-y-2">
          {MODULOS_USUARIO.map(id => (
            <div key={id} className="flex items-center gap-2">
              <Checkbox id={`mod-${id}`} checked={modulos.includes(id)} onCheckedChange={() => alternar(id)} data-testid={`checkbox-modulo-${id}`} />
              <Label htmlFor={`mod-${id}`}>{rotuloModulo(id)}</Label>
            </div>
          ))}
          <p className="text-xs text-muted-foreground">Produtor não fica sem Cacho; comprador não fica sem Mercado.</p>
        </div>
        <div className="flex justify-end border-t px-4 py-3">
          <Button onClick={salvarModulos} disabled={atualizar.isPending} data-testid="button-salvar-modulos">Salvar módulos</Button>
        </div>
      </DataCard>

      <Dialog open={aberto} onOpenChange={setAberto}>
        <DialogContent>
          <DialogHeader><DialogTitle>Cortesia — {f.nome}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            {([
              ["permanente", "Cortesia permanente"],
              ["ate", "Cortesia até uma data"],
              ["revogar", "Revogar (o trial vence agora)"],
            ] as const).map(([id, rotulo]) => (
              <label key={id} className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="radio" name="regime" checked={regime === id} onChange={() => setRegime(id)} data-testid={`radio-cortesia-${id}`} />
                {rotulo}
              </label>
            ))}
            {regime === "ate" && (
              <div className="space-y-1">
                <Label htmlFor="ate">Acesso liberado até</Label>
                <Input id="ate" type="date" value={ate} onChange={e => setAte(e.target.value)} data-testid="input-cortesia-ate" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAberto(false)}>Cancelar</Button>
            <Button onClick={salvarCortesia} disabled={cortesia.isPending} data-testid="button-salvar-cortesia">
              {cortesia.isPending ? "Salvando…" : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AbaUsuarios({ f }: { f: FichaFazendaDTO }) {
  const { user: eu } = useAuth();
  const { toast } = useToast();
  const acao = useAcaoUsuarioFazenda(f.id);
  const [link, setLink] = useState<{ link: LinkAcesso; nome: string; telefone: string | null } | null>(null);
  // D10-ish: a flag de plataforma só para usuários da MINHA org (a do Rizoma).
  const minhaOrg = !eu?.suporte && eu?.orgId === f.id;

  function agir(u: FichaFazendaDTO["usuarios"][number], tipo: "reset-link" | "desativar" | "reativar" | "plataforma-admin", corpo?: unknown) {
    acao.mutate({ userId: u.id, acao: tipo, corpo }, {
      onSuccess: r => {
        if (tipo === "reset-link" && r.convite) setLink({ link: r.convite, nome: u.name, telefone: u.telefone });
        else toast({ title: tipo === "desativar" ? "Usuário desativado" : tipo === "reativar" ? "Usuário reativado" : "Flag de plataforma atualizada" });
      },
      onError: (e: Error) => toast({ title: "Não deu", description: e.message, variant: "destructive" }),
    });
  }

  if (f.usuarios.length === 0) {
    return <EmptyState icone={Building2} titulo="Sem usuários" descricao="Ninguém entra nesta organização ainda — ela está pré-cadastrada." compacto testId="empty-usuarios-fazenda" />;
  }
  return (
    <>
      <DataCard titulo="Usuários" meta={`${f.usuariosAtivos} ativo(s) de ${f.usuariosTotal}`}>
        {f.usuarios.map(u => (
          <div key={u.id} className="flex flex-col gap-2 border-b px-4 py-3 last:border-b-0 md:flex-row md:items-center md:justify-between" data-testid={`usuario-fazenda-${u.id}`}>
            <div className="min-w-0">
              <p className={"font-medium truncate " + (!u.ativo ? "opacity-60" : "")}>{u.name}</p>
              <p className="text-xs text-muted-foreground truncate">{u.email ?? u.telefone ?? "—"} · {u.modulos ? u.modulos.map(rotuloModulo).join(" · ") : "todos os módulos"} · {fmtUltimoAcesso(u.ultimoLoginEm)}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0 flex-wrap">
              <Badge variant="outline" className="text-xs font-normal">{ROTULO_PAPEL[u.role] ?? u.role}</Badge>
              {u.plataformaAdmin && <StatusBadge tom="info">plataforma</StatusBadge>}
              {u.ativo ? <StatusBadge tom="sucesso" ponto>Ativo</StatusBadge> : <StatusBadge tom="neutro">Inativo</StatusBadge>}
              {u.ativo && (
                <Button size="sm" variant="outline" onClick={() => agir(u, "reset-link")} disabled={acao.isPending} data-testid={`button-reset-link-${u.id}`}>
                  <KeyRound className="h-3.5 w-3.5 mr-1" />Link de senha
                </Button>
              )}
              {u.ativo ? (
                <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => agir(u, "desativar")} disabled={acao.isPending} data-testid={`button-desativar-${u.id}`}>
                  <UserX className="h-3.5 w-3.5 mr-1" />Desativar
                </Button>
              ) : (
                <Button size="sm" variant="ghost" onClick={() => agir(u, "reativar")} disabled={acao.isPending} data-testid={`button-reativar-${u.id}`}>
                  <UserCheck className="h-3.5 w-3.5 mr-1" />Reativar
                </Button>
              )}
              {minhaOrg && u.id !== eu?.id && (
                <Button size="sm" variant="ghost" onClick={() => agir(u, "plataforma-admin", { valor: !u.plataformaAdmin })} disabled={acao.isPending} data-testid={`button-plataforma-admin-${u.id}`}>
                  <ShieldCheck className="h-3.5 w-3.5 mr-1" />{u.plataformaAdmin ? "Tirar plataforma" : "Dar plataforma"}
                </Button>
              )}
            </div>
          </div>
        ))}
      </DataCard>
      {link && <LinkAcessoDialog link={link.link} titulo="Link de senha" nome={link.nome} telefone={link.telefone} onFechar={() => setLink(null)} />}
    </>
  );
}

const ROTULO_STATUS_ACESSO: Record<string, string> = {
  PENDENTE: "Aguardando o admin", AUTORIZADO: "Autorizado", RECUSADO: "Recusado", ENCERRADO: "Encerrado", EXPIRADO: "Expirado",
};

function fmtDataHora(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

/** Histórico dos pedidos de acesso de suporte desta fazenda. */
function AbaAcessos({ f }: { f: FichaFazendaDTO }) {
  const acessos = useAcessosDaFazenda(f.id);
  const aguardando = estadoDaConsulta(acessos, { variante: "painel", linhas: 3 });
  if (aguardando) return aguardando;
  const lista = acessos.data ?? [];
  if (lista.length === 0) return <EmptyState titulo="Nenhum pedido de acesso" descricao="Use “Solicitar acesso” no cabeçalho para pedir ao admin desta fazenda." compacto testId="empty-acessos-fazenda" />;
  return (
    <DataCard titulo="Acessos de suporte" meta={`${lista.length} pedido(s)`}>
      {lista.map(a => (
        <div key={a.id} className="flex items-start justify-between gap-3 border-b px-4 py-2.5 last:border-b-0 text-sm" data-testid={`acesso-${a.id}`}>
          <div className="min-w-0">
            <p className="truncate">{a.motivo}</p>
            <p className="text-xs text-muted-foreground">
              pedido {fmtDataHora(a.criadoEm)} · {a.duracaoHoras} h{a.expiraEm ? ` · até ${fmtDataHora(a.expiraEm)}` : ""}
            </p>
          </div>
          <StatusBadge tom={a.status === "AUTORIZADO" ? "sucesso" : a.status === "PENDENTE" ? "atencao" : "neutro"} ponto={a.status === "AUTORIZADO" || a.status === "PENDENTE"}>
            {ROTULO_STATUS_ACESSO[a.status] ?? a.status}
          </StatusBadge>
        </div>
      ))}
    </DataCard>
  );
}

/**
 * O botão do cabeçalho: "Solicitar acesso" quando não há pedido vivo, "Entrar
 * como (leitura)" quando há um AUTORIZADO vigente. Some na org do próprio Hugo
 * (D10: ele já é ADMIN nativo) e em modo suporte.
 */
function BotaoEntrarComo({ f }: { f: FichaFazendaDTO }) {
  const { user: eu } = useAuth();
  const { toast } = useToast();
  const acessos = useAcessosDaFazenda(f.id);
  const solicitar = useSolicitarAcesso(f.id);
  const entrar = useEntrarComo();
  const [pedindo, setPedindo] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [horas, setHoras] = useState(24);

  if (!eu || eu.suporte || eu.orgId === f.id) return null;
  const lista = acessos.data ?? [];
  const vigente = lista.find((a: AcessoSuporteDTO) => a.status === "AUTORIZADO" && a.expiraEm && new Date(a.expiraEm).getTime() > Date.now());
  const pendente = lista.find((a: AcessoSuporteDTO) => a.status === "PENDENTE");

  if (vigente) {
    return (
      <Button
        onClick={() => entrar.mutate(vigente.id, {
          onSuccess: () => { window.location.assign("/"); },
          onError: (e: Error) => toast({ title: "Não deu para entrar", description: e.message, variant: "destructive" }),
        })}
        disabled={entrar.isPending}
        data-testid="button-entrar-como"
      >
        <Eye className="h-4 w-4 mr-2" />Entrar como (leitura) · até {fmtDataHora(vigente.expiraEm)}
      </Button>
    );
  }
  if (pendente) {
    return <Button variant="outline" disabled data-testid="button-acesso-pendente">Aguardando o admin autorizar</Button>;
  }
  return (
    <>
      <Button variant="outline" onClick={() => setPedindo(true)} data-testid="button-solicitar-acesso">
        <ShieldCheck className="h-4 w-4 mr-2" />Solicitar acesso
      </Button>
      <Dialog open={pedindo} onOpenChange={setPedindo}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Solicitar acesso de leitura</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="motivo">Motivo (o admin da fazenda vai ler)</Label>
              <Textarea id="motivo" rows={3} value={motivo} onChange={e => setMotivo(e.target.value)} data-testid="input-motivo-acesso" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="horas">Duração (horas, máx. 72)</Label>
              <Input id="horas" type="number" min={1} max={72} value={horas} onChange={e => setHoras(Number(e.target.value))} data-testid="input-horas-acesso" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPedindo(false)}>Cancelar</Button>
            <Button
              disabled={motivo.trim().length < 5 || solicitar.isPending}
              onClick={() => solicitar.mutate({ motivo: motivo.trim(), duracaoHoras: horas }, {
                onSuccess: () => { setPedindo(false); setMotivo(""); toast({ title: "Pedido enviado", description: "O admin da fazenda recebe o aviso no app (e por e-mail, se tiver)." }); },
                onError: (e: Error) => toast({ title: "Não deu", description: e.message, variant: "destructive" }),
              })}
              data-testid="button-enviar-pedido-acesso"
            >
              Pedir acesso
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function AbaAuditoria({ f }: { f: FichaFazendaDTO }) {
  if (f.auditoria.length === 0) {
    return <EmptyState titulo="Sem registros" descricao="Nenhuma ação auditada nesta organização ainda." compacto testId="empty-auditoria-fazenda" />;
  }
  return (
    <DataCard titulo="Auditoria" meta="últimos 50 registros">
      {f.auditoria.map(a => (
        <div key={a.id} className="flex items-start justify-between gap-3 border-b px-4 py-2.5 last:border-b-0 text-sm" data-testid={`audit-${a.id}`}>
          <div className="min-w-0">
            <p className="font-mono text-xs truncate">
              {a.action}
              {a.metadata && (a.metadata as { plataforma?: boolean }).plataforma && <span className="ml-2 text-muted-foreground">(plataforma)</span>}
            </p>
            <p className="text-xs text-muted-foreground truncate">{a.userEmail ?? "sistema"}{a.entityType ? ` · ${a.entityType}` : ""}</p>
          </div>
          <span className="text-xs text-muted-foreground tabular-nums shrink-0">
            {new Date(a.createdAt).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
          </span>
        </div>
      ))}
    </DataCard>
  );
}
